import React, { useState } from 'react'
import { css } from '@emotion/core'
import { createPortal } from 'react-dom'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import {
  Separator,
  List,
  ListItem,
  Row,
  Card,
  H3,
  Subheading,
  Button,
  useContainerWidth,
  colors,
  FlowGrid,
} from '@fs/zion-ui'
import { useTranslation, Trans } from 'react-i18next'
import { DocumentBasic, ControlDrag } from '@fs/zion-icon'
import { DndContext, DragOverlay } from '@dnd-kit/core'
import { getNestedOrFloatingElementById, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import Draggable, { dashedBorderCssStyle } from '../../../../../views/indexing/components/ui/Draggable'
import Droppable from '../../../../../views/indexing/components/ui/Droppable'

const iconCss = css`
  padding: 12px 8px;
  cursor: grab;
  align-self: center;
`

const draggableStyle = {
  display: 'flex',
  backgroundColor: colors.gray00,
  borderRadius: '6px',
}

const dragHandleStyle = {
  display: 'flex',
  backgroundColor: colors.blue30,
  marginLeft: '12px',
  borderTopRightRadius: '6px',
  borderBottomRightRadius: '6px',
}

const childrenStyle = {
  flexGrow: 1,
}

const flexItemCss = css`
  min-height: 200px;
  padding: 10px;
`

function DraggableRelationshipGroup({ name, otherName }) {
  return (
    <Draggable
      id="relationship"
      style={draggableStyle}
      dragHandleStyle={dragHandleStyle}
      childrenStyle={childrenStyle}
      dragHandle={
        <div css={iconCss}>
          <ControlDrag />
        </div>
      }
    >
      <List>
        <ListItem primaryText={name} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
        <ListItem primaryText={otherName} startElement={<ListItem.Icon Icon={DocumentBasic} />} />
      </List>
    </Draggable>
  )
}

function DroppableRecordArea({ id, relationships, selectedRecord, name, otherName }) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const [seeMore, setSeeMore] = useState(false)

  const relationshipsListCss = css`
    background-color: ${colors.gray00};
    border-radius: 6px;
    overflow-y: scroll;
    overflow-x: hidden;
  `

  const relationshipsToRender = seeMore ? relationships : relationships.slice(0, 2)

  return (
    <Droppable id={id}>
      <div style={dashedBorderCssStyle} css={flexItemCss}>
        <H3 size="H6">
          {t('edit-relationships.choose-record-overlay.recordX', 'Record {number}', {
            number: id === 'current' ? '1' : '2',
          })}
          <Subheading>{t('edit-relationships.choose-record-overlay.move-here', 'Move Relationship Here')}</Subheading>
        </H3>
        <Separator size="nano" />
        {selectedRecord === id && <DraggableRelationshipGroup name={name} otherName={otherName} />}
        <Separator size="xs" />
        <div css={relationshipsListCss}>
          <List>
            {relationshipsToRender.map((relationship) => {
              const relationshipPerson = getNestedOrFloatingElementById(state.images, relationship.otherPersonId)
              return (
                <ListItem
                  key={relationship.id}
                  overline={`${
                    relationshipPerson?.primary
                      ? `${t('edit-relationships.choose-record-overlay.principal', 'Principal')} | `
                      : ''
                  }${relationship.label}`}
                  primaryText={relationship.value}
                  startElement={<ListItem.Icon Icon={DocumentBasic} />}
                />
              )
            })}
          </List>
        </div>
        {relationships.length > 2 && (
          <Row alignX="center">
            <Button
              onClick={() => {
                setSeeMore((prev) => !prev)
              }}
            >
              {seeMore
                ? t('edit-relationships.choose-record-overlay.see-less.button', 'See Less')
                : t('edit-relationships.choose-record-overlay.see-more.button', 'See More')}
            </Button>
          </Row>
        )}
      </div>
    </Droppable>
  )
}

export default function ChooseRecordOverlayStepOne({
  person,
  otherPerson,
  record1Relationships,
  record2Relationships,
  selectedRecord,
  setSelectedRecord,
}) {
  const [dragInfo, setDragInfo] = useState(false)
  const name = getPersonName({ person })
  const otherName = getPersonName({ person: otherPerson })
  const atWidth = useContainerWidth()

  return (
    <>
      <Trans i18nKey="edit-relationships.choose-record-overlay.drag-instructions">
        <strong>Drag</strong>
        <i> {{ name }}</i> and
        <i> {{ otherName }}</i> to the correct record. Record names have to be in the same record to have relationships.
      </Trans>
      <Separator size="xs" />
      <DndContext
        onDragStart={() => {
          setDragInfo(true)
        }}
        onDragEnd={(event) => {
          setDragInfo(false)
          setSelectedRecord(event.over.id)
        }}
      >
        <Card backgroundColor={colors.blue03}>
          <FlowGrid
            guttersX="xs"
            columnCount={atWidth({
              default: 1,
              sm: 2,
            })}
          >
            <DroppableRecordArea
              relationships={record1Relationships}
              id="current"
              selectedRecord={selectedRecord}
              name={name}
              otherName={otherName}
            />
            <DroppableRecordArea
              relationships={record2Relationships}
              id="other"
              selectedRecord={selectedRecord}
              name={name}
              otherName={otherName}
            />
          </FlowGrid>
        </Card>
        {createPortal(
          <DragOverlay
            modifiers={[restrictToWindowEdges]}
            dropAnimation={null}
            style={{
              boxShadow: '0 0 5px rgb(68 68 68 / 0.6)',
              background: 'white',
              borderRadius: '6px',
            }}
          >
            {dragInfo && <DraggableRelationshipGroup name={name} otherName={otherName} />}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  )
}
